/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { Fragment, memo, useEffect, useRef, useState } from 'react';
import { toBase64 } from 'utils/to-base-64';
import Image from 'next/image';
import { TrashIconOutline } from '~/svgs';
import cn from 'classnames';
import { useTranslate } from '~/i18n';
import imageCompression from 'browser-image-compression';
import { sentryCaptureException } from '~/errors/sentry';

/* -------------------------------------------------------------------------- */
/*      You should accept props like onChange, maxSize, fileTypes, etc...     */
/* -------------------------------------------------------------------------- */

type FileDNDProps = {
   /** Defaulted to 5 files (-1 for unlimited) */
   maxFiles?: number;
   /** Defaulted to 4MB per file */
   maxFileSizeInMB?: number;
   onChange?: (files: File[]) => any;
   files?: File[];
   error?: React.ReactNode | any;
   setError?: (message: string) => void;
   images?: string | string[];
   /** This only works for images links (coming from images prop) */
   onImageDelete?: (index: number, current_index: number) => any;
};

const FileDND = ({
   maxFileSizeInMB = 4,
   maxFiles = 5,
   onChange: onFilesChange,
   files: f,
   error,
   setError,
   images: defaultImages,
   onImageDelete,
}: FileDNDProps) => {
   const input = useRef<HTMLInputElement>(null);
   const elm = useRef<HTMLDivElement>(null);
   const hover = useRef<HTMLDivElement>(null);
   const btn = useRef<HTMLButtonElement>(null);
   const [files, setFiles] = useState<File[]>(f || []);
   const [images, setImages] = useState<{ url: string; index?: number }[]>([]);

   const { translate } = useTranslate();
   const _MAX_SIZE = 1024 * 1024 * maxFileSizeInMB;

   const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.currentTarget.files?.[0];
      const options = {
         maxSizeMB: 1,
         maxIteration: 30,
      };
      if (!file) return;
      imageCompression(file, options)
         .then(async compressedFile => {
            if (compressedFile && compressedFile?.size <= _MAX_SIZE) {
               const base64 = await toBase64(compressedFile);
               setFiles(prev => [...prev, compressedFile]);
               setImages(prev => [...prev, { url: base64 }]);
            } else if (compressedFile && compressedFile.size >= _MAX_SIZE) {
               setError?.(translate({ id: 'createproduct:image size is too big' }));
            }
         })
         .catch(error => {
            console.log(error);
            sentryCaptureException(error);
         });
   };

   // When drop file on box; you could also accept multi files conditionally and loop through them
   const onDrop = async (e: React.DragEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      const options = {
         maxSizeMB: 1,
         maxIteration: 30,
      };
      if (!file) return;
      imageCompression(file, options)
         .then(async compressedFile => {
            if (compressedFile && compressedFile?.size <= _MAX_SIZE) {
               const base64 = await toBase64(compressedFile);
               setFiles(prev => [...prev, compressedFile]);
               setImages(prev => [...prev, { url: base64 }]);
            } else if (compressedFile && compressedFile.size >= _MAX_SIZE) {
               setError?.(translate({ id: 'createproduct:image size is too big' }));
            }
         })
         .catch(error => {
            console.log(error);
            sentryCaptureException(error);
         });

      // on drop, hide the placeholder
      if (hover.current) {
         hover.current!.style.opacity = `0`;
      }
      if (btn.current) {
         btn.current.classList.remove('border-primary');
      }
   };

   useEffect(() => {
      (async function () {
         const items: { url: string; index: number }[] = [];
         if (f) {
            for (let i = 0; i < f.length; i++) {
               items.push({ url: await toBase64(f[i]), index: i });
            }
            setImages(prev => [...prev, ...items]);
         }
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      const list = defaultImages
         ? typeof defaultImages === 'string'
            ? [{ url: defaultImages, index: 0 }]
            : defaultImages.map((url, index) => ({ url, index }))
         : [];
      setImages(prev => [...prev, ...list]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      onFilesChange?.(files);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [files]);

   // When drag above box
   const onDragOver = (e: React.DragEvent<HTMLButtonElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const { clientX, clientY } = e;

      const x = clientX - rect.left;
      const y = clientY - rect.top;

      /* X and Y are half of the mouse placeholder width and height,
       * you could pass them via props too
       */
      if (hover.current) {
         hover.current.style.transform = `translate(${x - 16}px, ${y - 16}px)`;
         hover.current.style.opacity = `1`;
      }
      e.stopPropagation();
      e.preventDefault();
      if (btn.current) {
         btn.current.classList.add('border-primary');
      }
   };

   // When drag event leaves the box
   const onDragLeave = (e: React.DragEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (hover.current) {
         hover.current.style.opacity = `0`;
      }
      if (btn.current) {
         btn.current.classList.remove('border-primary');
      }
   };

   const deleteImage = (index: number, imageStoredIndex?: number) => {
      const clone = [...images];
      const filesClone = [...files];
      clone.splice(index, 1);
      setImages(clone);
      if (typeof imageStoredIndex !== 'undefined') {
         onImageDelete?.(imageStoredIndex, index);
      } else {
         filesClone.splice(index, 1);
         setFiles(filesClone);
      }
   };

   const showAddButton = maxFiles >= 0 ? images.length < maxFiles : true;

   return (
      <Fragment>
         <div className="flex flex-wrap">
            {showAddButton && (
               <button
                  ref={btn}
                  type="button"
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onClick={() => input.current?.click()}
                  className={cn(
                     'dd rounded-md focus:outline-none flex justify-center items-center relative w-[100px] h-[100px] border focus:border-primary hover:border-primary',
                     error ? 'border-red-500' : 'border-gray-300'
                  )}>
                  <div
                     ref={hover}
                     style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        opacity: '0',
                        backgroundColor: 'rgba(0,0,0,.5)',
                     }}></div>
                  <div ref={elm} className="flex flex-wrap">
                     <input
                        id="drop-photos"
                        onChange={onChange}
                        ref={input}
                        type="file"
                        onClick={e => (e.currentTarget.value = '')}
                        className="hidden"
                        accept="image/jpeg,image/gif,image/png,image/x-eps"
                     />
                     <img alt="" src="/icons/add-circle-icon.svg" />
                  </div>
               </button>
            )}
            {images.map((img, index) => (
               <ImageComponent
                  key={index}
                  index={index}
                  img={img.url}
                  deleteImage={i => deleteImage(i, img.index)}
               />
            ))}
         </div>
         {error && <div className="mt-1 text-xs text-states-error">{error}</div>}
      </Fragment>
   );
};

type ImageProps = {
   index: number;
   img: string;
   deleteImage: (n: number) => any;
};
const ImageComponent = ({ index, deleteImage, img }: ImageProps) => {
   return (
      <div key={index} className="relative mb-2 rtl:mr-2 ltr:ml-2">
         <div className="absolute z-10 inset-0 flex justify-center items-center ">
            <button
               onClick={() => deleteImage(index)}
               type="button"
               className="text-white  bg-dark-red  rounded-full shadow-lg focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-primary w-8 h-8 flex items-center justify-center">
               <TrashIconOutline className="[&>path]:fill-white" />
            </button>
         </div>
         <Image
            width="100%"
            height="100%"
            alt=""
            src={img}
            className="rounded-md object-cover "
            quality={40}
         />
      </div>
   );
};

export default memo(FileDND);
